import { Box, Button } from '@mui/material';
import { Controller } from 'react-hook-form';
import { EditFnaStepComponent } from '../types';
import { FNAParty } from '../../../common/types/analyses';
import { JSX, useState } from 'react';
import { useSnackbar } from 'notistack';
import PartyList from '../../../parties/partyList';
import SelectPartiesDialog from './selectPartiesDialog';

/**
 * Step with FNA parties
 */
const PartiesStep: EditFnaStepComponent = ({ fna, control, commitFnaChanges }): JSX.Element => {
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenDialog = (): void => {
    if (fna.roleplayers.length === 0) {
      enqueueSnackbar('Please pick a client first', { variant: 'warning' });
      return;
    }
    setSelectDialogOpen(true);
  };

  const alreadySelectedClients = fna.parties
    .flatMap((party) => [party.partyRoleplayerID, party.representativeRoleplayerID])
    .filter(Boolean);

  return (
    <Controller
      name="parties"
      control={control}
      render={({ field: { onChange, value } }): JSX.Element => (
        <Box>
          <Box className="flex justify-end">
            <Button variant="outlined" onClick={handleOpenDialog}>
              Choose parties
            </Button>
          </Box>
          <PartyList<FNAParty>
            parties={fna.parties}
            onDelete={(partyForDelete): void => {
              onChange(fna.parties.filter(({ id }) => id !== partyForDelete.id));
              commitFnaChanges();
            }}
            onChange={(updatedParty): void => {
              const updatedParties = [...fna.parties];
              const partyIndex = updatedParties.findIndex(({ id }) => id === updatedParty.id);
              if (partyIndex !== -1) {
                updatedParties[partyIndex] = updatedParty;
                onChange(updatedParties);
                commitFnaChanges();
              }
            }}
          />
          <SelectPartiesDialog
            open={selectDialogOpen}
            forbiddenRoleplayerIDs={alreadySelectedClients}
            onClose={(selectedParties): void => {
              const roleplayerID = fna.roleplayers[0]?.roleplayerID;
              // if some parties was selected, update fna's parties field
              if (selectedParties && roleplayerID) {
                onChange([
                  ...value,
                  ...selectedParties.map((selectedParty) => ({
                    ...selectedParty,
                    fnaid: fna.id,
                    roleplayerID,
                    // FNA parties are always secondary
                    isPrimary: false,
                  })),
                ]);
              }
              commitFnaChanges();
              setSelectDialogOpen(false);
            }}
          />
        </Box>
      )}
    />
  );
};

export default PartiesStep;
